/**
 * Raw user data from backend /auth/me endpoint
 */
export interface Auth0MeUser {
  sub: string;
  email?: string;
  name?: string;
  picture?: string;
}

/**
 * Auth0 user type with additional convenience properties
 */
export interface Auth0User {
  id: string;
  email?: string;
  name?: string;
  picture?: string;
  /**
   * Auth0 connection name parsed from `sub` (e.g. 'auth0', 'google-oauth2',
   * 'microsoft-graph'). See deriveConnectionFromSub for sub formats.
   */
  connection?: string;
  raw: Auth0MeUser;
}

/**
 * Auth0 session containing tokens and expiration info
 */
export interface Auth0Session {
  /** JWT access token for API calls */
  access_token: string;
  /** ID token containing user claims */
  id_token?: string;
  /** Token expiration timestamp (Unix epoch) */
  expires_at: number;
  /** Refresh token for obtaining new access tokens */
  refresh_token?: string;
}

/**
 * Parse the Auth0 connection name from a sub claim.
 *
 * Auth0 sub formats:
 * - Built-in strategies: `<provider>|<idp_id>` (e.g. `auth0|abc`, `google-oauth2|abc`)
 * - Custom OAuth2 connections: `oauth2|<connection>|<idp_id>`
 *   (e.g. `oauth2|microsoft-graph|c1d761d4-...`)
 */
function deriveConnectionFromSub(sub: string | undefined): string | undefined {
  if (!sub) return undefined;
  const parts = sub.split('|');
  if (parts.length < 2 || !parts[0]) return undefined;
  if (parts[0] === 'oauth2') {
    if (parts.length >= 3 && parts[1]) return parts[1];
    return undefined;
  }
  return parts[0];
}

/**
 * Convert backend /auth/me user to our Auth0User type
 */
export function mapAuth0User(auth0User: Auth0MeUser | undefined): Auth0User | null {
  if (!auth0User) return null;
  return {
    id: auth0User.sub || '',
    email: auth0User.email,
    name: auth0User.name,
    picture: auth0User.picture,
    connection: deriveConnectionFromSub(auth0User.sub),
    raw: auth0User,
  };
}
